/**
 * Singleton API client for Catalog module
 *
 * Centralizes all API calls with proper typing.
 * Uses Infrastructure's request helper.
 */

import { request } from '../../../Infrastructure'
import { getProductAttributes, getAutoRelatedSkus, getAutoRelatedProducts } from '../queries'

import type { ProductAttribute, AutoRelatedProduct } from '../types'

// Exported response data types for hooks
export interface ProductAttributesData {
  products: {
    items: ProductAttribute[]
  }
}

export interface AutoRelatedSkusData {
  getAutoRelatedSkus: { sku: string }[]
}

export interface AutoRelatedProductsData {
  products: {
    items: AutoRelatedProduct[]
  }
}

/**
 * Catalog API client
 *
 * Note: request<T>() returns Promise<{ data: T }>, so we unwrap to return T directly
 */
export const catalogApi = {
  /**
   * Fetch product attributes by SKU
   */
  getProductAttributes: async (sku: string): Promise<ProductAttributesData> => {
    const response = await request<ProductAttributesData>(getProductAttributes, { sku })
    return response.data
  },

  /**
   * Fetch auto-related product SKUs
   */
  getAutoRelatedSkus: async (sku: string): Promise<AutoRelatedSkusData> => {
    const response = await request<AutoRelatedSkusData>(getAutoRelatedSkus, { sku })
    return response.data
  },

  /**
   * Fetch auto-related products by SKUs
   */
  getAutoRelatedProducts: async (skus: string[]): Promise<AutoRelatedProductsData> => {
    const response = await request<AutoRelatedProductsData>(getAutoRelatedProducts, { skus })
    return response.data
  },
}
